import { api, API_BASE, ApiError } from "@/lib/apiClient";

/* ─────────────── Types ─────────────── */

export interface HeroCustomFieldValue {
  schemaId: number;
  propertyUuid: string;
  value: string | number | boolean | null;
}

export interface JahrOut {
  jahr: number;
  is_historisch: boolean;
}

export interface OrdnerOut {
  id: number;
  nummer: string;
  beschreibung: string;
  jahr: number;
  kunde_id?: number | null;
  kunden_code?: number | null;
  kunden_name?: string | null;
  branch_id?: number | null;
  measure_id?: number | null;
  project_type_id?: number | null;
  project_step_id?: number | null;
  branch_name?: string | null;
  measure_name?: string | null;
  project_type_name?: string | null;
  project_step_name?: string | null;
  hero_custom_field_defaults?: HeroCustomFieldValue[] | null;
  /** Bei Projekterstellung fragen, ob beauftragt wurde. */
  ask_commission_after_create?: boolean;
  /** Bei Projekterstellung Schadensmeldung / FM-Auftrag hochladen erlauben. */
  allow_schadensmeldung_on_create?: boolean;
}

export interface OrdnerIn {
  nummer: string;
  beschreibung: string;
  jahr: number;
  branch_id?: number | null;
  measure_id?: number | null;
  project_type_id?: number | null;
  project_step_id?: number | null;
  hero_custom_field_defaults?: HeroCustomFieldValue[] | null;
  ask_commission_after_create?: boolean;
  allow_schadensmeldung_on_create?: boolean;
}

export interface KundeOut {
  id: number;
  code: number;
  name: string;
  use_travel_eighths: boolean;
  show_time_range_on_timesheet: boolean;
  use_hero_customer_name_in_projects: boolean;
  travel_eighths_hero_nr?: string | null;
}

export interface KundeIn {
  code: number;
  name: string;
  use_travel_eighths?: boolean;
  show_time_range_on_timesheet?: boolean;
  use_hero_customer_name_in_projects?: boolean;
  travel_eighths_hero_nr?: string | null;
}

export interface StandardMaterialOut {
  id: number;
  text: string;
  menge: number;
  einheit?: string | null;
  bemerkung?: string | null;
  hero_nr?: string | null;
  hero_id?: number | null;
  artikel_id?: number | null;
  kunde_id?: number;
  ordner_id?: number;
  sordner_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface StandardMaterialIn {
  text: string;
  menge?: number;
  einheit?: string | null;
  bemerkung?: string | null;
  hero_nr?: string | null;
  hero_id?: number | null;
  artikel_id?: number | null;
}

export interface SOrdnerOut {
  id: number;
  nummer: string;
  beschreibung: string;
  kunde_id?: number | null;
  last_auftrag_nummer?: number | null;
  jahr?: number | null;
  hero_customer_id?: number | null;
  geschaeftspartner_id?: number | null;
  hero_address_id?: number | null;
  ort_name_text?: string | null;
  kunden_code?: number | null;
  kunden_name?: string | null;
  ort_name?: string | null;
  sharepoint_folder_id?: string | null;
  rename_summary?: Record<string, unknown> | null;
}

export interface SOrdnerIn {
  nummer: string;
  beschreibung: string;
  kunde_id?: number | null;
  last_auftrag_nummer?: number | null;
  jahr?: number | null;
  hero_customer_id?: number | null;
  geschaeftspartner_id?: number | null;
  hero_address_id?: number | null;
  ort_name_text?: string | null;
}

export interface HeroCustomer {
  id: number;
  company_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  code?: number | null;
}

export interface HeroCustomFieldSchema {
  schemaId: number;
  schemaName?: string;
  properties?: Array<{
    propertyUuid: string;
    label?: string;
    type?: string;
  }>;
}

export interface HeroCustomFieldConfigItem {
  schemaId: number;
  propertyUuid: string;
  enabled?: boolean;
  showOnTimesheet?: boolean;
  showOnFolderDefaults?: boolean;
}

export interface RateMapOut {
  id: number | null;
  kunden_id: number | null;
  hero_nr: string;
  artikel_id?: number | null;
  kunden_label?: string | null;
  kunden_company_name?: string | null;
  kunden_first_name?: string | null;
  kunden_last_name?: string | null;
  artikel_name?: string | null;
  artikel_unit?: string | null;
}

export interface RateMapIn {
  kunden_id: number | null;
  hero_nr: string;
  artikel_id?: number | null;
}

export interface HeroStepAction {
  action_key: string;
  label?: string;
  key?: string;
}

export interface HeroStepMapping {
  action_key: string;
  project_type_id: number;
  step_id: number;
}

export interface HeroStepActionMapResponse {
  actions: HeroStepAction[];
  mappings: HeroStepMapping[];
}

export interface HeroSupplyText {
  id: number;
  name?: string;
  text?: string;
}

export interface PrintLocation {
  id: number;
  name: string;
  printer_name: string;
}

export interface OrderEmailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  is_default?: boolean;
}

export interface AnfrageSnippet {
  id: number;
  name: string;
  subject: string;
  body: string;
  sort_order: number;
}

export interface UserSession {
  sid: string;
  user_login: string;
  user_display_name?: string | null;
  created_at?: string | null;
  last_seen_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  user_agent?: string | null;
  ip?: string | null;
}

export interface MailSignatureData {
  signature_html: string;
  preview_html?: string;
  placeholders?: Array<{ key: string; desc?: string }>;
  default_template?: string;
  is_global?: boolean;
}

export interface HeroProductInfo {
  name?: string;
  hero_nr?: string;
  unit?: string;
}

/** Hero `/product-by-nr` liefert oft `base_data.name` statt top-level `name`. */
export function parseHeroProductInfo(raw: unknown): HeroProductInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Record<string, unknown>;
  const bd = (p.base_data && typeof p.base_data === "object"
    ? p.base_data
    : {}) as Record<string, unknown>;
  const name = String(bd.name || p.name || "").trim();
  const hero_nr = String(p.nr || p.hero_nr || "").trim();
  const unit = String(bd.unit_type || p.unit_type || p.unit || "").trim();
  if (!name && !hero_nr) return null;
  return { name: name || hero_nr, hero_nr, unit: unit || undefined };
}

export interface CodeMapEntry {
  hero_contact_id: number;
  code: number;
  deleted?: boolean;
}

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const q = sp.toString();
  return q ? `?${q}` : "";
}

/* ─────────────── Boot / Sync ─────────────── */

export async function syncHeroAnsprechpartner(): Promise<void> {
  await api.get("/hero/sync-hero-ansprechpartner");
}

/* ─────────────── Jahre ─────────────── */

export async function listJahre(): Promise<JahrOut[]> {
  const data = await api.get<JahrOut[]>("/jahre");
  return Array.isArray(data) ? data : [];
}

export async function createJahr(body: { jahr: number; is_historisch?: boolean }): Promise<JahrOut> {
  return api.post<JahrOut>("/jahre", body);
}

/* ─────────────── Ordner ─────────────── */

export async function listOrdnerJahre(): Promise<number[]> {
  const data = await api.get<number[]>("/ordner/jahre");
  return Array.isArray(data) ? data : [];
}

export function historischOrdnerTemplateDownloadUrl(jahr: number): string {
  return `${API_BASE}/ordner/historisch/template.xlsx?jahr=${jahr}`;
}

export async function importHistorischOrdnerExcel(jahr: number, file: File): Promise<unknown> {
  const form = new FormData();
  form.append("file", file);
  return api.postForm(`/ordner/historisch/import-excel?jahr=${jahr}`, form);
}

export function historischAuftragTemplateDownloadUrl(ordnerId: number): string {
  return `${API_BASE}/ordner/${ordnerId}/auftraege/template.xlsx`;
}

export async function importHistorischAuftragExcel(ordnerId: number, file: File): Promise<unknown> {
  const form = new FormData();
  form.append("file", file);
  return api.postForm(`/ordner/${ordnerId}/auftraege/import-excel`, form);
}

export async function listOrdner(jahr: number): Promise<OrdnerOut[]> {
  const data = await api.get<OrdnerOut[]>(`/ordner${buildQuery({ jahr })}`);
  return Array.isArray(data) ? data : [];
}

export async function getOrdner(id: number): Promise<OrdnerOut> {
  return api.get<OrdnerOut>(`/ordner/${id}`);
}

export async function createOrdner(body: OrdnerIn): Promise<OrdnerOut> {
  return api.post<OrdnerOut>("/ordner", body);
}

export async function updateOrdner(id: number, body: OrdnerIn): Promise<OrdnerOut> {
  return api.put<OrdnerOut>(`/ordner/${id}`, body);
}

export async function deleteOrdner(id: number): Promise<void> {
  await api.del(`/ordner/${id}`);
}

export async function copyOrdnerSelected(srcJahr: number, dstJahr: number, ordnerIds: number[]): Promise<void> {
  await api.post("/ordner/copy-selected", { src_jahr: srcJahr, dst_jahr: dstJahr, ordner_ids: ordnerIds });
}

export async function listOrdnerStandardMaterials(ordnerId: number): Promise<StandardMaterialOut[]> {
  const data = await api.get<StandardMaterialOut[]>(`/ordner/${ordnerId}/standardmaterialien`);
  return Array.isArray(data) ? data : [];
}

export async function createOrdnerStandardMaterial(ordnerId: number, body: StandardMaterialIn): Promise<StandardMaterialOut> {
  return api.post<StandardMaterialOut>(`/ordner/${ordnerId}/standardmaterialien`, body);
}

export async function updateOrdnerStandardMaterial(
  ordnerId: number,
  materialId: number,
  body: StandardMaterialIn,
): Promise<StandardMaterialOut> {
  return api.put<StandardMaterialOut>(`/ordner/${ordnerId}/standardmaterialien/${materialId}`, body);
}

export async function deleteOrdnerStandardMaterial(ordnerId: number, materialId: number): Promise<void> {
  await api.del(`/ordner/${ordnerId}/standardmaterialien/${materialId}`);
}

export async function listSOrdnerStandardMaterials(sordnerId: number): Promise<StandardMaterialOut[]> {
  const data = await api.get<StandardMaterialOut[]>(`/sordner/${sordnerId}/standardmaterialien`);
  return Array.isArray(data) ? data : [];
}

export async function createSOrdnerStandardMaterial(
  sordnerId: number,
  body: StandardMaterialIn,
): Promise<StandardMaterialOut> {
  return api.post<StandardMaterialOut>(`/sordner/${sordnerId}/standardmaterialien`, body);
}

export async function updateSOrdnerStandardMaterial(
  sordnerId: number,
  materialId: number,
  body: StandardMaterialIn,
): Promise<StandardMaterialOut> {
  return api.put<StandardMaterialOut>(`/sordner/${sordnerId}/standardmaterialien/${materialId}`, body);
}

export async function deleteSOrdnerStandardMaterial(sordnerId: number, materialId: number): Promise<void> {
  await api.del(`/sordner/${sordnerId}/standardmaterialien/${materialId}`);
}

/* ─────────────── Kunden ─────────────── */

export async function listKunden(): Promise<KundeOut[]> {
  const data = await api.get<KundeOut[]>("/kunden");
  return Array.isArray(data) ? data : [];
}

export async function createKunde(body: KundeIn): Promise<KundeOut> {
  return api.post<KundeOut>("/kunden", body);
}

export async function updateKunde(id: number, body: KundeIn): Promise<KundeOut> {
  return api.put<KundeOut>(`/kunden/${id}`, body);
}

export async function deleteKunde(id: number): Promise<void> {
  await api.del(`/kunden/${id}`);
}

/** App-Kunde (Geschäftspartner), der einem Ernsthaus-Kundencode zugewiesen ist. */
export interface KundenGeschaeftspartnerOut {
  id: number;
  hero_contact_id?: number | null;
  firmenname: string;
  kundencode?: number | null;
  kundennummer?: string | null;
  ort?: string | null;
  is_deleted?: boolean;
}

export async function listKundenGeschaeftspartner(
  kundeId: number,
): Promise<KundenGeschaeftspartnerOut[]> {
  const data = await api.get<KundenGeschaeftspartnerOut[]>(
    `/kunden/${kundeId}/geschaeftspartner`,
  );
  return Array.isArray(data) ? data : [];
}

export async function assignKundenGeschaeftspartner(
  kundeId: number,
  geschaeftspartnerId: number,
): Promise<KundenGeschaeftspartnerOut> {
  return api.post<KundenGeschaeftspartnerOut>(`/kunden/${kundeId}/geschaeftspartner`, {
    geschaeftspartner_id: geschaeftspartnerId,
  });
}

export async function unassignKundenGeschaeftspartner(
  kundeId: number,
  gpId: number,
): Promise<KundenGeschaeftspartnerOut> {
  return api.del<KundenGeschaeftspartnerOut>(
    `/kunden/${kundeId}/geschaeftspartner/${gpId}`,
  );
}

export async function listKundenStandardMaterials(kundeId: number): Promise<StandardMaterialOut[]> {
  const data = await api.get<StandardMaterialOut[]>(`/kunden/${kundeId}/standardmaterialien`);
  return Array.isArray(data) ? data : [];
}

export async function createKundenStandardMaterial(kundeId: number, body: StandardMaterialIn): Promise<StandardMaterialOut> {
  return api.post<StandardMaterialOut>(`/kunden/${kundeId}/standardmaterialien`, body);
}

export async function updateKundenStandardMaterial(
  kundeId: number,
  materialId: number,
  body: StandardMaterialIn,
): Promise<StandardMaterialOut> {
  return api.put<StandardMaterialOut>(`/kunden/${kundeId}/standardmaterialien/${materialId}`, body);
}

export async function deleteKundenStandardMaterial(kundeId: number, materialId: number): Promise<void> {
  await api.del(`/kunden/${kundeId}/standardmaterialien/${materialId}`);
}

export async function getHeroCustomerCodeMap(): Promise<CodeMapEntry[]> {
  const data = await api.get<CodeMapEntry[]>("/hero/customer-code-map");
  return Array.isArray(data) ? data : [];
}

export async function saveHeroCustomerCodeMap(entries: CodeMapEntry[]): Promise<void> {
  await api.post("/hero/customer-code-map", entries);
}

/* ─────────────── S-Ordner ─────────────── */

export async function listSOrdner(): Promise<SOrdnerOut[]> {
  const data = await api.get<SOrdnerOut[]>("/sordner");
  return Array.isArray(data) ? data : [];
}

export async function getSOrdner(id: number): Promise<SOrdnerOut> {
  return api.get<SOrdnerOut>(`/sordner/${id}`);
}

export async function createSOrdner(body: SOrdnerIn): Promise<SOrdnerOut> {
  return api.post<SOrdnerOut>("/sordner", body);
}

export async function updateSOrdner(id: number, body: SOrdnerIn): Promise<SOrdnerOut> {
  return api.put<SOrdnerOut>(`/sordner/${id}`, body);
}

export async function deleteSOrdner(id: number): Promise<void> {
  await api.del(`/sordner/${id}`);
}

export async function updateSOrdnerLastAuftrag(id: number, lastAuftragNummer: number): Promise<void> {
  await api.patch(`/sordner/${id}/last-auftrag`, { last_auftrag_nummer: lastAuftragNummer });
}

export async function getSOrdnerSharepointUrl(id: number): Promise<string | null> {
  try {
    const data = await api.get<{ url?: string }>(`/sordner/${id}/sharepoint-url`);
    return data?.url?.trim() || null;
  } catch {
    return null;
  }
}

export async function linkSOrdnerSharepoint(id: number): Promise<void> {
  await api.post(`/sordner/${id}/sharepoint-link`, {});
}

export async function createSOrdnerSharepoint(id: number): Promise<void> {
  await api.post(`/sordner/${id}/sharepoint-create`, {});
}

export function sOrdnerTemplateDownloadUrl(): string {
  return `${API_BASE}/sordner/template.xlsx`;
}

/** Lädt eine API-Datei mit Session-Cookie. Ein neuer Tab gilt als Direktaufruf und liefert client_required. */
export async function downloadAuthenticatedFile(url: string, filename: string): Promise<void> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    let message = `Download fehlgeschlagen (${res.status})`;
    try {
      const body = (await res.json()) as { detail?: unknown };
      if (typeof body?.detail === "string" && body.detail.trim() && body.detail !== "client_required") {
        message = body.detail.trim();
      }
    } catch {
      // Antwort war keine JSON-Fehlermeldung.
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export function sOrdnerAuftragTemplateFilename(nummer: string): string {
  const safe = nummer.replace(/[^\p{L}\p{N}_-]+/gu, "_").replace(/^_+|_+$/g, "").slice(0, 40);
  return `sordner-auftraege-${safe || "sordner"}.xlsx`;
}

export async function importSOrdnerExcel(file: File): Promise<unknown> {
  const form = new FormData();
  form.append("file", file);
  return api.postForm("/sordner/import-excel", form);
}

export function sOrdnerAuftragTemplateDownloadUrl(sordnerId: number): string {
  return `${API_BASE}/sordner/${sordnerId}/auftraege/template.xlsx`;
}

export interface SOrdnerAuftragPrompt {
  sordner_id: number;
  sordner_nummer: string;
  prompt: string;
}

export function fetchSOrdnerAuftragPrompt(sordnerId: number): Promise<SOrdnerAuftragPrompt> {
  return api.get<SOrdnerAuftragPrompt>(`/sordner/${sordnerId}/auftraege/ki-prompt`);
}

export interface SOrdnerAuftragImportRow {
  line: number;
  ok: boolean;
  duplicate?: boolean;
  message?: string;
  warnings?: string[];
  auftragsnummer?: string;
  auftrag_id?: number | null;
}

export interface SOrdnerAuftragImportResult {
  ok: number;
  fail: number;
  rows: SOrdnerAuftragImportRow[];
}

export async function importSOrdnerAuftragExcel(
  sordnerId: number,
  file: File,
): Promise<SOrdnerAuftragImportResult> {
  const form = new FormData();
  form.append("file", file);
  return api.postForm<SOrdnerAuftragImportResult>(
    `/sordner/${sordnerId}/auftraege/import-excel`,
    form,
  );
}

/* ─────────────── Hero ─────────────── */

export async function listHeroKunden(q?: string): Promise<HeroCustomer[]> {
  const data = await api.get<HeroCustomer[]>(`/hero/kunden${buildQuery({ q })}`);
  return Array.isArray(data) ? data : [];
}

export async function listHeroCustomFieldSchemas(): Promise<HeroCustomFieldSchema[]> {
  const data = await api.get<HeroCustomFieldSchema[]>("/hero/custom-fields/schemas");
  return Array.isArray(data) ? data : [];
}

export async function saveHeroCustomFieldConfig(items: HeroCustomFieldConfigItem[]): Promise<void> {
  await api.post("/hero/custom-fields/config", items);
}

export async function listHourlyRates(): Promise<RateMapOut[]> {
  const data = await api.get<RateMapOut[]>("/hero/customer-hourly-rates");
  return Array.isArray(data) ? data : [];
}

export async function upsertHourlyRate(body: RateMapIn): Promise<RateMapOut> {
  return api.post<RateMapOut>("/hero/customer-hourly-rates", body);
}

export async function deleteHourlyRate(entryId: number): Promise<void> {
  await api.del(`/hero/customer-hourly-rates/${entryId}`);
}

export async function listTravelKmRates(): Promise<RateMapOut[]> {
  const data = await api.get<RateMapOut[]>("/hero/customer-travel-km");
  return Array.isArray(data) ? data : [];
}

export async function upsertTravelKmRate(body: RateMapIn): Promise<RateMapOut> {
  return api.post<RateMapOut>("/hero/customer-travel-km", body);
}

export async function deleteTravelKmRate(entryId: number): Promise<void> {
  await api.del(`/hero/customer-travel-km/${entryId}`);
}

export async function getHeroProductByNr(heroNr: string): Promise<HeroProductInfo | null> {
  try {
    const raw = await api.get<unknown>(`/hero/product-by-nr/${encodeURIComponent(heroNr)}`);
    return parseHeroProductInfo(raw);
  } catch {
    return null;
  }
}

export async function listHeroSupplyTexts(): Promise<HeroSupplyText[]> {
  const data = await api.get<HeroSupplyText[]>("/hero/supply-texts");
  return Array.isArray(data) ? data : [];
}

export async function getHeroStepActionMap(): Promise<HeroStepActionMapResponse> {
  return api.get<HeroStepActionMapResponse>("/auftraege/settings/hero-step-action-map");
}

export async function saveHeroStepActions(items: Array<{ action_key: string; label: string }>): Promise<void> {
  await api.post("/auftraege/settings/hero-step-actions", { items });
}

export async function saveHeroStepActionMap(items: HeroStepMapping[]): Promise<void> {
  await api.post("/auftraege/settings/hero-step-action-map", { items });
}

export async function deleteHeroStepAction(actionKey: string): Promise<void> {
  await api.del(`/auftraege/settings/hero-step-actions/${encodeURIComponent(actionKey)}`);
}

export async function getInvoiceTextIds(): Promise<number[]> {
  const data = await api.get<{ ids?: number[]; invoice_draft_supply_text_ids?: number[] }>(
    "/auftraege/settings/invoice-texts",
  );
  return data?.ids ?? data?.invoice_draft_supply_text_ids ?? [];
}

export async function saveInvoiceTextIds(ids: number[]): Promise<void> {
  await api.post("/auftraege/settings/invoice-texts", { invoice_draft_supply_text_ids: ids });
}

/* ─────────────── Templates ─────────────── */

export async function getTimesheetTemplate(): Promise<string> {
  const data = await api.get<{ html?: string }>("/timesheets/template");
  return data?.html ?? "";
}

export async function saveTimesheetTemplate(html: string): Promise<void> {
  await api.post("/timesheets/template", { html });
}

export async function getStuecklisteTemplate(): Promise<string> {
  const data = await api.get<{ html?: string }>("/stuecklisten/template");
  return data?.html ?? "";
}

export async function saveStuecklisteTemplate(html: string): Promise<void> {
  await api.post("/stuecklisten/template", { html });
}

export async function getProjectCoverTemplate(): Promise<string> {
  const data = await api.get<{ html?: string }>("/auftraege/cover-template");
  return data?.html ?? "";
}

export async function saveProjectCoverTemplate(html: string): Promise<void> {
  await api.post("/auftraege/cover-template", { html });
}

export async function getOrderCoverTemplate(): Promise<{ html: string; one_per_bnr?: boolean }> {
  const data = await api.get<{ html?: string; one_per_bnr?: boolean }>("/orders/cover-template");
  return { html: data?.html ?? "", one_per_bnr: data?.one_per_bnr };
}

export async function saveOrderCoverTemplate(html: string, onePerBnr?: boolean): Promise<void> {
  await api.post("/orders/cover-template", { html, one_per_bnr: onePerBnr });
}

export async function resetOrderCoverTemplate(): Promise<void> {
  await api.post("/orders/cover-template", { reset: true });
}

export async function getDoorMaintenanceTemplate(): Promise<string> {
  const data = await api.get<{ html?: string }>("/tuerwartung/template");
  return data?.html ?? "";
}

export async function saveDoorMaintenanceTemplate(html: string): Promise<void> {
  await api.post("/tuerwartung/template", { html });
}

export async function getDoorIssueTemplate(): Promise<string> {
  const data = await api.get<{ html?: string }>("/tuerwartung/project-issue-overview-template");
  return data?.html ?? "";
}

export async function saveDoorIssueTemplate(html: string): Promise<void> {
  await api.post("/tuerwartung/project-issue-overview-template", { html });
}

export async function uploadDoorIssueTemplate(file: File): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  await api.postForm("/tuerwartung/project-issue-overview-template/upload", form);
}

/* ─────────────── Mail / Kommunikation ─────────────── */

export async function listOrderEmailTemplates(): Promise<OrderEmailTemplate[]> {
  const data = await api.get<OrderEmailTemplate[]>("/orders/email-templates");
  return Array.isArray(data) ? data : [];
}

export async function createOrderEmailTemplate(body: Omit<OrderEmailTemplate, "id">): Promise<OrderEmailTemplate> {
  return api.post<OrderEmailTemplate>("/orders/email-templates", body);
}

export async function updateOrderEmailTemplate(id: number, body: Partial<OrderEmailTemplate>): Promise<OrderEmailTemplate> {
  return api.put<OrderEmailTemplate>(`/orders/email-templates/${id}`, body);
}

export async function deleteOrderEmailTemplate(id: number): Promise<void> {
  await api.del(`/orders/email-templates/${id}`);
}

export async function listAnfrageSnippets(): Promise<AnfrageSnippet[]> {
  const data = await api.get<AnfrageSnippet[]>("/anfragen/snippet-templates");
  return Array.isArray(data) ? data : [];
}

export async function createAnfrageSnippet(body: Omit<AnfrageSnippet, "id">): Promise<AnfrageSnippet> {
  return api.post<AnfrageSnippet>("/anfragen/snippet-templates", body);
}

export async function updateAnfrageSnippet(id: number, body: Partial<AnfrageSnippet>): Promise<AnfrageSnippet> {
  return api.patch<AnfrageSnippet>(`/anfragen/snippet-templates/${id}`, body);
}

export async function deleteAnfrageSnippet(id: number): Promise<void> {
  await api.del(`/anfragen/snippet-templates/${id}`);
}

export async function getMailSignature(): Promise<MailSignatureData> {
  return api.get<MailSignatureData>("/me/mail-signature");
}

export async function saveMailSignature(signatureHtml: string): Promise<void> {
  await api.put("/me/mail-signature", { signature_html: signatureHtml });
}

/* ─────────────── Druck ─────────────── */

export type {
  PrintJobFeedback,
  PrintJobResult,
  PrintJobStatus,
  PrintQueueResponse,
} from "@/lib/printJob";
export {
  PrintJobFeedbackError,
  formatPrintJobFeedback,
  getPrintJob,
  printByLocationAndWait,
  printTestPage,
  printTestPageAndWait,
  queuePrintByLocation,
  runPrintByLocationWithFeedback,
  runPrintTestPageWithFeedback,
  showPrintJobFeedback,
  waitForPrintJob,
} from "@/lib/printJob";

export async function listPrinters(): Promise<string[]> {
  const data = await api.get<string[] | { printers?: string[] }>("/print/printers");
  if (Array.isArray(data)) return data;
  return data?.printers ?? [];
}

export async function listPrintLocations(): Promise<PrintLocation[]> {
  const data = await api.get<PrintLocation[]>("/print/locations");
  return Array.isArray(data) ? data : [];
}

export async function createPrintLocation(name: string, printerName: string): Promise<PrintLocation> {
  return api.post<PrintLocation>(
    `/print/locations${buildQuery({ name, printer_name: printerName })}`,
    {},
  );
}

export async function deletePrintLocation(id: number): Promise<void> {
  await api.del(`/print/locations/${id}`);
}

export interface CupsQueueJob {
  job_id: string;
  printer: string;
  user: string;
  size_bytes?: number | null;
  submitted_at?: string;
  state?: string | null;
  status_text?: string;
  alerts?: string[];
  reasons?: string[];
  issues?: string[];
}

export interface CupsQueuePrinter {
  id?: string;
  name: string;
  state?: string | null;
  status?: string | null;
  enabled?: boolean | null;
  issues?: string[];
}

export interface CupsQueueResponse {
  jobs: CupsQueueJob[];
  printers: CupsQueuePrinter[];
}

export async function listPrintQueue(printer?: string): Promise<CupsQueueResponse> {
  const query = printer ? buildQuery({ printer }) : "";
  return api.get<CupsQueueResponse>(`/print/queue${query}`, { cache: "no-store" });
}

export async function cancelPrintQueueJob(cupsJobId: string): Promise<{ ok?: boolean; job_id?: string }> {
  const body = new URLSearchParams();
  body.set("cups_job_id", cupsJobId);
  return api.post("/print/queue/cancel", undefined, { rawBody: body });
}

export async function cancelAllPrintQueueJobs(printer?: string): Promise<{ ok?: boolean; printer?: string | null }> {
  const body = new URLSearchParams();
  if (printer) body.set("printer", printer);
  return api.post("/print/queue/cancel-all", undefined, { rawBody: body });
}

export async function restartPrintQueueJob(cupsJobId: string): Promise<{ ok?: boolean; job_id?: string; action?: string }> {
  const body = new URLSearchParams();
  body.set("cups_job_id", cupsJobId);
  return api.post("/print/queue/restart", undefined, { rawBody: body });
}

export interface PrintTestPageResponse {
  ok?: boolean;
  job_id?: string;
  status?: string;
  printer_name?: string;
  kind?: string;
}

export interface CupsPrinterOptionChoice {
  value: string;
  factory_default?: boolean;
}

export interface CupsPrinterOption {
  key: string;
  label: string;
  choices: CupsPrinterOptionChoice[];
  factory_default?: string | null;
  value?: string | null;
  is_custom?: boolean;
}

export interface CupsPrinterOptionsResponse {
  printer: string;
  options: CupsPrinterOption[];
  defaults?: Record<string, string>;
}

export async function getPrinterOptions(printerName: string): Promise<CupsPrinterOptionsResponse> {
  return api.get<CupsPrinterOptionsResponse>(
    `/print/printers/${encodeURIComponent(printerName)}/options`,
    { cache: "no-store" },
  );
}

export async function savePrinterOptions(
  printerName: string,
  options: Record<string, string | null>,
): Promise<CupsPrinterOptionsResponse> {
  return api.post<CupsPrinterOptionsResponse>(
    `/print/printers/${encodeURIComponent(printerName)}/options`,
    { options },
  );
}

/* ─────────────── Sessions ─────────────── */

export async function listUserSessions(params: {
  user_login?: string;
  include_revoked?: boolean;
  include_expired?: boolean;
  limit?: number;
}): Promise<UserSession[]> {
  const data = await api.get<{ sessions?: UserSession[] }>(
    `/admin/sessions${buildQuery(params)}`,
  );
  return data?.sessions ?? [];
}

export async function revokeSession(sid: string, reason?: string): Promise<void> {
  await api.post(`/admin/sessions/${encodeURIComponent(sid)}/revoke`, { reason: reason ?? "" });
}

export async function revokeUserSessions(userLogin: string, reason?: string, keepSelf?: boolean): Promise<void> {
  await api.post(`/admin/sessions/users/${encodeURIComponent(userLogin)}/revoke`, {
    reason: reason ?? "",
    keep_self: keepSelf ?? true,
  });
}

export async function revokeAllSessions(reason?: string, keepSelf?: boolean): Promise<void> {
  await api.post("/admin/sessions/revoke-all", { reason: reason ?? "", keep_self: keepSelf ?? true });
}
